import { useState } from 'preact/hooks';

export interface WorkflowExecution {
  id: string;
  rule_id: string;
  triggered_at: string;
  trigger_event: Record<string, unknown> | null;
  result: string;
  result_detail: string | null;
}

export interface WorkflowRuleData {
  id: string;
  name: string;
  condition_type: string;
  condition_config: Record<string, unknown>;
  action_type: string;
  action_config: Record<string, unknown>;
  is_active: number;
  created_at: string;
  updated_at: string;
}

interface WorkflowRuleProps {
  rule: WorkflowRuleData;
  executions: WorkflowExecution[];
  onToggleActive: (id: string, active: boolean) => void;
  onEdit: (rule: WorkflowRuleData) => void;
  onDelete: (id: string) => void;
}

const CONDITION_LABELS: Record<string, string> = {
  service_down: '🔴 Service Down',
  security_alert: '🛡️ Security Alert',
  disk_threshold: '💾 Disk-Schwellwert',
  manual: '✋ Manuell',
};

const ACTION_LABELS: Record<string, string> = {
  restart: '🔄 Restart',
  telegram_message: '📨 Telegram-Nachricht',
  escalation: '🚨 Eskalation',
};

export function WorkflowRule({ rule, executions, onToggleActive, onEdit, onDelete }: WorkflowRuleProps) {
  const [showHistory, setShowHistory] = useState(false);

  return (
    <div class={`workflow-rule ${rule.is_active ? '' : 'workflow-rule-inactive'}`}>
      <div class="workflow-rule-header">
        <div class="workflow-rule-info">
          <span class="workflow-rule-name">{rule.name}</span>
          <span class="workflow-rule-meta">
            {CONDITION_LABELS[rule.condition_type] ?? rule.condition_type}
            {' → '}
            {ACTION_LABELS[rule.action_type] ?? rule.action_type}
          </span>
        </div>
        <label class="toggle" title={rule.is_active ? 'Aktiv' : 'Inaktiv'}>
          <input
            type="checkbox"
            checked={!!rule.is_active}
            onChange={() => onToggleActive(rule.id, !rule.is_active)}
          />
          <span class="toggle-slider" />
        </label>
      </div>

      <div class="workflow-rule-actions">
        <button class="btn-icon" onClick={() => onEdit(rule)} title="Bearbeiten">✏️</button>
        <button class="btn-icon btn-delete" onClick={() => onDelete(rule.id)} title="Löschen">🗑️</button>
        <button
          class="btn-icon"
          onClick={() => setShowHistory(!showHistory)}
          title="Historie"
        >
          {showHistory ? '▲' : '▼'} ({executions.length})
        </button>
      </div>

      {showHistory && (
        <div class="workflow-history">
          {executions.length === 0 ? (
            <p class="empty-hint">Noch keine Ausführungen.</p>
          ) : (
            <ul class="history-list">
              {executions.map((exec) => (
                <li key={exec.id} class={`history-item history-${exec.result}`}>
                  <span class="history-time">
                    {new Date(exec.triggered_at).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })}
                  </span>
                  <span class={`history-badge history-badge-${exec.result}`}>
                    {exec.result === 'success' ? '✅' : exec.result === 'failure' ? '❌' : '⏭️'}
                    {exec.result}
                  </span>
                  {exec.result_detail && (
                    <span class="history-detail">{exec.result_detail}</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
